import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DwAutoUpdateCheckerService, DwMessage } from '@devwareapps/devware-cap';
import { asyncScheduler, Subscription } from 'rxjs';
import { observeOn } from 'rxjs/operators';

@Component({
  selector: 'streakers-security-state',
  template: `<div *ngIf="showError"><dw-message [message]="errorMessage"></dw-message></div>
    <div *ngIf="showSecurityMessage && !showError" class="security-state" role="status" aria-live="polite">
      <span class="spinner" aria-hidden="true"></span><p>{{ friendlyMessage }}</p>
    </div>`,
  styles: [`:host { display: block; } .security-state { min-height: 45vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 20px; padding: 32px 20px; color: #123b2f; } p { margin: 0; font-size: 20px; font-weight: 600; text-align: center; } .spinner { width: 36px; height: 36px; border: 3px solid #e5dfcf; border-top-color: #e5a000; border-radius: 50%; animation: state-spin 1s linear infinite; } @keyframes state-spin { to { transform: rotate(360deg); } } @media (prefers-reduced-motion: reduce) { .spinner { animation: none; } }`]
})
export class StreakersSecurityStateComponent {
  @Input() showSecurityMessage: boolean;
  @Input() securityMessage: string;
  @Input() showError: boolean;
  @Input() errorMessage: DwMessage;

  get friendlyMessage(): string {
    const message = this.securityMessage || '';
    if (/emulating\s+/i.test(message)) { return message.replace(/emulating\s+/i, 'Switching to ') + '…'; }
    if (/log(?:ging)?\s*out|sign(?:ing)?\s*out/i.test(message)) { return 'Signing you out…'; }
    if (/log(?:ging)?\s*in|sign(?:ing)?\s*in|authenticat/i.test(message)) { return 'Signing you in…'; }
    return 'Getting things ready…';
  }
}

@Component({
  selector: 'streakers-offline-state',
  templateUrl: './streakers-offline-state.component.html',
  styleUrls: ['./streakers-offline-state.component.scss']
})
export class StreakersOfflineStateComponent implements AfterViewInit {
  @ViewChild('retryButton') retryButton: ElementRef<HTMLButtonElement>;
  ngAfterViewInit(): void { this.retryButton.nativeElement.focus({ preventScroll: true }); }
  retry(): void { window.location.reload(); }
}

@Component({
  selector: 'streakers-update-notice',
  template: `<details *ngIf="available" class="update-notice">
    <summary><span role="status">New version ready</span></summary>
    <div class="update-help">Finish what you’re doing, then hard refresh your browser to get the latest version.
      <p>Windows: <kbd>Ctrl + Shift + R</kbd></p>
      <p>Mac: use your browser’s option to reload without cached files.</p>
    </div>
  </details>`,
  styles: [`:host { display: contents; } .update-notice { position: relative; color: #fff3cd; font-size: 12px; } summary { padding: 8px 0; cursor: pointer; white-space: nowrap; } summary:focus-visible { outline: 2px solid #fff3cd; outline-offset: 4px; } .update-help { position: absolute; top: 100%; right: 0; z-index: 1050; width: 280px; max-width: calc(100vw - 32px); padding: 16px; border-radius: 8px; background: #fff8e7; color: #123b2f; box-shadow: 0 6px 24px #0002; font-size: 14px; line-height: 1.5; } p { margin: 12px 0 0; } kbd { background: #123b2f; color: #fff8e7; }`]
})
export class StreakersUpdateNoticeComponent implements OnInit, OnDestroy {
  available = false;
  private _subscription: Subscription;
  constructor(private _updates: DwAutoUpdateCheckerService) {}
  ngOnInit(): void {
    const updates = this._updates.autoUpdateChanged();
    // CAP may disable update checking; defer cached emissions past shell initialization.
    this._subscription = updates?.pipe(observeOn(asyncScheduler)).subscribe(value => this.available = value);
  }
  ngOnDestroy(): void { this._subscription?.unsubscribe(); }
}
